/**
 * A API padroniza erro em ProblemDetails (RFC 7807) e o FluentValidation
 * devolve 400 com os campos antes de o controller rodar. Este arquivo traduz
 * isso para o que a tela precisa: uma mensagem para mostrar e, quando houver,
 * a lista de campos para marcar.
 */

export type ProblemDetails = {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  /** Do FluentValidation: { nomeDoCampo: ["mensagem", ...] } */
  errors?: Record<string, string[]>
}

export type ErroDeCampo = { campo: string; texto: string }

export class ErroDaApi extends Error {
  readonly status: number
  readonly campos: ErroDeCampo[]
  readonly problema: ProblemDetails | null

  constructor(mensagem: string, status: number, campos: ErroDeCampo[], problema: ProblemDetails | null) {
    super(mensagem)
    this.name = "ErroDaApi"
    this.status = status
    this.campos = campos
    this.problema = problema
  }

  /** 409: vínculo que impede a operação. A tela mostra a lista, não um erro genérico. */
  get ehConflito() { return this.status === 409 }
  get ehNaoAutenticado() { return this.status === 401 }
  get ehSemPermissao() { return this.status === 403 }
  get ehNaoEncontrado() { return this.status === 404 }
}

/** Erro que a trava do canário levanta. Nunca chega à rede. */
export class ErroDeTrava extends Error {
  constructor(mensagem: string) {
    super(mensagem)
    this.name = "ErroDeTrava"
  }
}

function pareceProblemDetails(x: unknown): x is ProblemDetails {
  return typeof x === "object" && x !== null && ("title" in x || "detail" in x || "errors" in x)
}

/**
 * Quando o servidor recusa **sem corpo**.
 *
 * A policy do ASP.NET responde 403 vazio — nenhum ProblemDetails —, e aí a
 * mensagem que sobrava era a do axios: "Request failed with status code 403".
 * Isso não diz nada a quem está do outro lado, e numa recusa de permissão é
 * justamente a hora de dizer o que fazer.
 */
function semCorpo(status: number): string | null {
  if (status === 400) return "Requisição inválida. Verifique os dados enviados."
  if (status === 401) return "Sua sessão expirou ou não foi reconhecida. Entre de novo."
  if (status === 403) return "Você não tem permissão para esta operação. Fale com quem administra os acessos."
  if (status === 404) return "O servidor não achou o que foi pedido."
  if (status >= 500) return "O servidor falhou nesta operação."
  return null
}

export function traduzirErro(status: number, corpo: unknown, textoDeRede: string): ErroDaApi {
  if (!pareceProblemDetails(corpo)) {
    return new ErroDaApi(
      semCorpo(status) || textoDeRede || "Não foi possível falar com o servidor.",
      status, [], null)
  }

  const campos: ErroDeCampo[] = []
  for (const [campo, textos] of Object.entries(corpo.errors ?? {})) {
    for (const texto of textos) campos.push({ campo: minusculaInicial(campo), texto })
  }

  const mensagem =
    campos.length > 0
      ? campos.map((c) => c.texto).join("\n")
      : corpo.detail ||
        (corpo.title !== "One or more validation errors occurred." ? corpo.title : null) ||
        semCorpo(status) ||
        textoDeRede ||
        "Erro no servidor."

  return new ErroDaApi(mensagem, status, campos, corpo)
}

/** O ASP.NET devolve a chave do campo em PascalCase; a tela trabalha em camelCase. */
function minusculaInicial(s: string): string {
  const partes = s.split(".")
  return partes.map((p) => (p ? p.charAt(0).toLowerCase() + p.slice(1) : p)).join(".")
}
